import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HexColorPicker } from "react-colorful";
import {
    Sun, Moon, Leaf, Monitor, ChevronDown, ChevronRight,
    AlertTriangle, Check, RotateCcw, Save, X, Wand2, ArrowLeft,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { contrastRatio, suggestAaFix } from "../lib/contrast";
import { api, formatApiError } from "../lib/api";

/**
 * Iter59.p24 — Theme Management page.
 *
 * Left column: 4 mode tiles + accordion of colour-picker overrides.
 * Right column: live preview of a sample dashboard so the user can
 * see the effect of every change immediately. Save/Cancel/Reset are
 * gated by WCAG AA contrast — if a picked colour drops below 4.5:1
 * against its natural background, we show a warning + "Fix
 * automatically" that snaps to the nearest AA-compliant shade.
 */

const MODES = [
    { key: "light",   label: "Light",          Icon: Sun,     desc: "Bright surfaces, high contrast text." },
    { key: "dark",    label: "Dark",           Icon: Moon,    desc: "Slate surfaces, low glare." },
    { key: "comfort", label: "Eye Comfort",    Icon: Leaf,    desc: "Warm sepia, reduced blue light." },
    { key: "system",  label: "System Default", Icon: Monitor, desc: "Follows your OS setting." },
];

/**
 * Token catalog exposed to the picker. Grouped for the accordion UI.
 * `bg` is the token the value is contrast-checked against (from the
 * currently resolved theme). Only text-family tokens have a `bg`;
 * surface / chart / status tokens don't need a contrast check.
 */
const TOKEN_GROUPS = [
    {
        key: "surfaces",
        label: "Surfaces",
        tokens: [
            { name: "--surface-page",     label: "Page background" },
            { name: "--surface-card",     label: "Card background" },
            { name: "--surface-elevated", label: "Elevated surface" },
        ],
    },
    {
        key: "text",
        label: "Text",
        tokens: [
            { name: "--text-primary",   label: "Primary text",   bg: "--surface-card" },
            { name: "--text-secondary", label: "Secondary text", bg: "--surface-card" },
            { name: "--text-muted",     label: "Muted text",     bg: "--surface-card" },
        ],
    },
    {
        key: "charts",
        label: "Charts",
        tokens: [
            { name: "--chart-grid",     label: "Grid lines" },
            { name: "--chart-series-1", label: "Series 1" },
            { name: "--chart-series-2", label: "Series 2" },
            { name: "--chart-series-3", label: "Series 3" },
            { name: "--chart-series-4", label: "Series 4" },
            { name: "--chart-series-5", label: "Series 5" },
            { name: "--chart-series-6", label: "Series 6" },
        ],
    },
    {
        key: "statuses",
        label: "Status colors",
        tokens: [
            { name: "--status-success", label: "Success" },
            { name: "--status-warning", label: "Warning" },
            { name: "--status-error",   label: "Error"   },
            { name: "--status-info",    label: "Info"    },
            { name: "--status-pending", label: "Pending" },
        ],
    },
    {
        key: "kpi",
        label: "KPI",
        tokens: [
            { name: "--kpi-number-color", label: "Number color", bg: "--surface-card" },
            { name: "--kpi-icon-color",   label: "Icon color" },
            { name: "--kpi-label-color",  label: "Label color",  bg: "--surface-card" },
        ],
    },
    {
        key: "buttons",
        label: "Buttons & Links",
        tokens: [
            { name: "--button-primary-bg",   label: "Primary button" },
            { name: "--button-primary-text", label: "Primary button text", bg: "--button-primary-bg" },
            { name: "--link-color",          label: "Link color",          bg: "--surface-card" },
        ],
    },
];

function currentTokenValue(name) {
    if (typeof document === "undefined") return "#000000";
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function hexOnly(color) {
    // react-colorful HexColorPicker only supports hex. Coerce
    // rgb/rgba shorthand to a best-effort hex so the picker doesn't
    // crash on non-hex tokens (e.g. rgba borders).
    if (!color) return "#000000";
    if (color.startsWith("#") && (color.length === 4 || color.length === 7)) {
        return color.length === 4
            ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
            : color;
    }
    return "#000000";
}

export default function ThemeSettings() {
    const navigate = useNavigate();
    const { mode: liveMode, customTokens: liveTokens, setMode, setCustomTokens, previewOnly, reset } = useTheme();
    const { user } = useAuth();

    // Draft state — changes apply as a live preview but do NOT persist
    // until the user clicks Save.
    const [draftMode, setDraftMode] = useState(liveMode);
    const [draftTokens, setDraftTokens] = useState(liveTokens || {});
    const [expanded, setExpanded] = useState({});
    const [openPicker, setOpenPicker] = useState(null);
    const [saving, setSaving] = useState(false);

    // Apply the draft to the DOM as a live preview.
    useEffect(() => {
        previewOnly({ mode: draftMode, customTokens: draftTokens });
    }, [draftMode, draftTokens, previewOnly]);

    // When the user leaves the page without saving, restore whatever
    // was persisted.
    useEffect(() => () => {
        previewOnly({ mode: liveMode, customTokens: liveTokens || {} });
    }, [liveMode, liveTokens, previewOnly]);

    // Resolve current colour for a token — draft override wins.
    const resolvedTokenValue = (name) => {
        if (draftTokens[name]) return draftTokens[name];
        return currentTokenValue(name) || "#000000";
    };

    // Compute contrast errors across all currently overridden text
    // tokens. Only tokens with a `bg` in the catalog are checked.
    const contrastIssues = useMemo(() => {
        const issues = [];
        for (const group of TOKEN_GROUPS) {
            for (const t of group.tokens) {
                if (!t.bg) continue;
                if (!(t.name in draftTokens)) continue; // only checks user changes
                const fg = draftTokens[t.name];
                const bg = draftTokens[t.bg] || currentTokenValue(t.bg);
                const ratio = contrastRatio(fg, bg);
                if (ratio != null && ratio < 4.5) {
                    issues.push({
                        token: t.name,
                        against: t.bg,
                        label: t.label,
                        ratio: Math.round(ratio * 100) / 100,
                        fg,
                        bg,
                        suggestion: suggestAaFix(fg, bg),
                    });
                }
            }
        }
        return issues;
    }, [draftTokens]);

    const dirty = draftMode !== liveMode
        || JSON.stringify(draftTokens) !== JSON.stringify(liveTokens || {});
    const canSave = dirty && contrastIssues.length === 0;

    const onSave = async () => {
        setSaving(true);
        try {
            await api.patch("/me/preferences", {
                mode: draftMode,
                custom_tokens: draftTokens,
            });
            // Reflect canonical values in the ThemeProvider (also
            // updates localStorage).
            await setMode(draftMode);
            await setCustomTokens(draftTokens);
            toast.success("Theme saved to your profile");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const onCancel = () => {
        setDraftMode(liveMode);
        setDraftTokens(liveTokens || {});
        previewOnly({ mode: liveMode, customTokens: liveTokens || {} });
        toast.info("Changes discarded");
    };

    const onReset = async () => {
        setDraftMode("system");
        setDraftTokens({});
        await reset();
        toast.success("Theme reset to defaults");
    };

    const setToken = (name, value) => {
        setDraftTokens((prev) => ({ ...prev, [name]: value }));
    };
    const clearToken = (name) => {
        setDraftTokens((prev) => {
            const { [name]: _, ...rest } = prev;
            return rest;
        });
    };
    const applyAll = () => {
        setDraftTokens((prev) => {
            const next = { ...prev };
            for (const issue of contrastIssues) next[issue.token] = issue.suggestion;
            return next;
        });
        toast.success(`Auto-fixed ${contrastIssues.length} contrast issue${contrastIssues.length === 1 ? "" : "s"}`);
    };

    return (
        <div className="min-h-screen" data-testid="theme-settings-page"
            style={{ background: "var(--surface-page)", color: "var(--text-primary)" }}>
            {/* Header */}
            <header
                className="px-8 py-4 flex items-center gap-3"
                style={{
                    background: "var(--surface-elevated)",
                    borderBottom: "1px solid var(--border-default)",
                }}
            >
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="h-9 w-9 rounded-full flex items-center justify-center transition"
                    style={{ color: "var(--text-secondary)" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "var(--menu-hover-bg)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    aria-label="Back"
                    data-testid="theme-back-btn"
                >
                    <ArrowLeft className="h-4 w-4" />
                </button>
                <div>
                    <div className="text-page-title" style={{ color: "var(--text-primary)", fontSize: 22 }}>
                        Theme Management
                    </div>
                    <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                        Preview any change instantly. Save writes to your profile ({user?.email || "…"}).
                    </div>
                </div>
            </header>

            <main className="asl-theme-grid" data-testid="theme-settings-main">
                {/* Left column: controls */}
                <aside className="asl-theme-controls" data-testid="theme-controls">
                    <section className="asl-theme-section">
                        <div className="text-label mb-3">Mode</div>
                        <div className="asl-theme-modes">
                            {MODES.map((m) => {
                                const Icon = m.Icon;
                                const active = draftMode === m.key;
                                return (
                                    <button
                                        key={m.key}
                                        type="button"
                                        onClick={() => setDraftMode(m.key)}
                                        className="asl-theme-mode-tile"
                                        data-testid={`theme-mode-tile-${m.key}`}
                                        style={{
                                            borderColor: active ? "var(--link-color)" : "var(--border-default)",
                                            background: active ? "var(--menu-selected-bg)" : "var(--surface-card)",
                                        }}
                                    >
                                        <Icon className="h-5 w-5" style={{ color: active ? "var(--link-color)" : "var(--text-secondary)" }} />
                                        <div className="text-left">
                                            <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{m.label}</div>
                                            <div className="text-xs" style={{ color: "var(--text-muted)" }}>{m.desc}</div>
                                        </div>
                                        {active && (
                                            <Check className="h-4 w-4 ml-auto" style={{ color: "var(--link-color)" }} />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <section className="asl-theme-section">
                        <div className="text-label mb-3">Advanced customization</div>
                        {TOKEN_GROUPS.map((g) => {
                            const isOpen = !!expanded[g.key];
                            return (
                                <div key={g.key} className="asl-theme-accordion">
                                    <button
                                        type="button"
                                        onClick={() => setExpanded((prev) => ({ ...prev, [g.key]: !prev[g.key] }))}
                                        className="asl-theme-accordion__head"
                                        data-testid={`theme-group-${g.key}`}
                                    >
                                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                        <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{g.label}</span>
                                        <span className="text-xs ml-auto" style={{ color: "var(--text-muted)" }}>
                                            {g.tokens.filter((t) => t.name in draftTokens).length > 0
                                                ? `${g.tokens.filter((t) => t.name in draftTokens).length} custom`
                                                : "default"}
                                        </span>
                                    </button>
                                    {isOpen && (
                                        <div className="asl-theme-accordion__body">
                                            {g.tokens.map((t) => {
                                                const val = resolvedTokenValue(t.name);
                                                const bg = t.bg ? (draftTokens[t.bg] || currentTokenValue(t.bg)) : null;
                                                const ratio = t.bg ? contrastRatio(val, bg) : null;
                                                const failing = ratio != null && ratio < 4.5;
                                                return (
                                                    <div key={t.name} className="asl-theme-token">
                                                        <button
                                                            type="button"
                                                            onClick={() => setOpenPicker(openPicker === t.name ? null : t.name)}
                                                            className="asl-theme-swatch"
                                                            style={{ background: val, borderColor: failing ? "var(--status-error)" : "var(--border-default)" }}
                                                            data-testid={`theme-swatch-${t.name.replace(/^--/, "")}`}
                                                            aria-label={`Pick ${t.label}`}
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-sm" style={{ color: "var(--text-primary)" }}>{t.label}</div>
                                                            <div className="mono text-[11px]" style={{ color: "var(--text-muted)" }}>{val}</div>
                                                            {t.bg && ratio != null && (
                                                                <div className="text-[11px] mt-0.5 flex items-center gap-1"
                                                                    style={{ color: failing ? "var(--status-error)" : "var(--status-success)" }}>
                                                                    {failing
                                                                        ? <><AlertTriangle className="h-3 w-3" /> {ratio.toFixed(2)}:1 — below AA</>
                                                                        : <><Check className="h-3 w-3" /> {ratio.toFixed(2)}:1 — AA</>
                                                                    }
                                                                </div>
                                                            )}
                                                        </div>
                                                        {t.name in draftTokens && (
                                                            <button
                                                                type="button"
                                                                onClick={() => clearToken(t.name)}
                                                                className="text-[11px]"
                                                                style={{ color: "var(--text-muted)" }}
                                                                data-testid={`theme-reset-${t.name.replace(/^--/, "")}`}
                                                            >
                                                                reset
                                                            </button>
                                                        )}
                                                        {openPicker === t.name && (
                                                            <div className="asl-theme-picker-pop">
                                                                <HexColorPicker
                                                                    color={hexOnly(val)}
                                                                    onChange={(hex) => setToken(t.name, hex)}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={val}
                                                                    onChange={(e) => setToken(t.name, e.target.value)}
                                                                    className="w-full mt-2 px-2 py-1 rounded text-xs mono"
                                                                    style={{
                                                                        background: "var(--input-bg)",
                                                                        color: "var(--input-text)",
                                                                        border: "1px solid var(--input-border)",
                                                                    }}
                                                                    data-testid={`theme-hex-${t.name.replace(/^--/, "")}`}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="text-[11px] mt-2"
                                                                    style={{ color: "var(--link-color)" }}
                                                                    onClick={() => setOpenPicker(null)}
                                                                >
                                                                    Close
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </section>

                    {contrastIssues.length > 0 && (
                        <section className="asl-theme-section asl-theme-issues" data-testid="theme-contrast-issues">
                            <div className="flex items-center gap-2 mb-2" style={{ color: "var(--status-error)" }}>
                                <AlertTriangle className="h-4 w-4" />
                                <div className="text-sm font-semibold">
                                    {contrastIssues.length} contrast issue{contrastIssues.length === 1 ? "" : "s"}
                                </div>
                            </div>
                            <ul className="text-xs space-y-1" style={{ color: "var(--text-secondary)" }}>
                                {contrastIssues.map((i) => (
                                    <li key={i.token}>
                                        <span className="mono">{i.label}</span> — {i.ratio}:1 (below 4.5:1)
                                    </li>
                                ))}
                            </ul>
                            <Button
                                size="sm"
                                variant="outline"
                                className="mt-3"
                                onClick={applyAll}
                                data-testid="theme-autofix-btn"
                            >
                                <Wand2 className="h-3.5 w-3.5 mr-1" /> Fix automatically
                            </Button>
                        </section>
                    )}

                    <section className="asl-theme-actions">
                        <Button
                            size="sm"
                            onClick={onSave}
                            disabled={!canSave || saving}
                            data-testid="theme-save-btn"
                            style={{
                                background: "var(--button-primary-bg)",
                                color: "var(--button-primary-text)",
                                opacity: !canSave || saving ? 0.55 : 1,
                            }}
                        >
                            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : "Save to Profile"}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={onCancel}
                            disabled={!dirty}
                            data-testid="theme-cancel-btn"
                        >
                            <X className="h-3.5 w-3.5 mr-1" /> Cancel
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={onReset}
                            data-testid="theme-reset-btn"
                        >
                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset to defaults
                        </Button>
                    </section>
                </aside>

                {/* Right column: live preview */}
                <section className="asl-theme-preview" data-testid="theme-preview-panel">
                    <div className="text-label mb-3">Live preview</div>
                    <ThemePreview />
                </section>
            </main>
        </div>
    );
}

/**
 * Sample UI slice — a KPI card, a small table, a chart-like stack of
 * status pills, a form input, a button row, and a sidebar item so
 * the user can see every category react to their picks in real time.
 */
function ThemePreview() {
    return (
        <div className="asl-theme-sample" data-testid="theme-preview-content">
            {/* KPI cards */}
            <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                    { label: "Active Workers", value: "80" },
                    { label: "Weekly Cost",    value: "$28,405" },
                    { label: "Open Exceptions", value: "0" },
                ].map((k) => (
                    <div key={k.label} className="rounded-lg p-3"
                        style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)", boxShadow: "var(--shadow-sm)" }}>
                        <div className="text-label" style={{ color: "var(--kpi-label-color)" }}>{k.label}</div>
                        <div className="mono font-bold mt-1" style={{ color: "var(--kpi-number-color)", fontSize: 22 }}>{k.value}</div>
                    </div>
                ))}
            </div>

            {/* Sample data table */}
            <div className="rounded-lg overflow-hidden mb-4"
                style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)" }}>
                <table className="w-full text-sm">
                    <thead>
                        <tr>
                            {["Employee", "Module", "Status", "Cost"].map((h) => (
                                <th key={h} className="text-left px-3 py-2 uppercase tracking-wider text-[10px]"
                                    style={{ background: "var(--table-header-bg)", color: "var(--table-header-text)" }}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {[
                            { n: "Axmad Ali",   m: "IC", s: "success", cost: "$1,240" },
                            { n: "Fartuun H.",  m: "AP", s: "warning", cost: "$960"   },
                            { n: "Yusuf Omar",  m: "CN", s: "info",    cost: "$1,320" },
                        ].map((row, i) => (
                            <tr key={row.n} style={{ background: i % 2 ? "var(--table-row-bg-alt)" : "var(--table-row-bg)", borderTop: "1px solid var(--table-border)" }}>
                                <td className="px-3 py-2" style={{ color: "var(--text-primary)" }}>{row.n}</td>
                                <td className="px-3 py-2 mono text-xs" style={{ color: "var(--text-secondary)" }}>{row.m}</td>
                                <td className="px-3 py-2">
                                    <span className="text-[11px] px-2 py-0.5 rounded-full"
                                        style={{
                                            background: `var(--status-${row.s}-bg)`,
                                            color: `var(--status-${row.s})`,
                                        }}>
                                        {row.s}
                                    </span>
                                </td>
                                <td className="px-3 py-2 mono text-right" style={{ color: "var(--text-primary)" }}>{row.cost}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Chart palette preview */}
            <div className="rounded-lg p-3 mb-4"
                style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)" }}>
                <div className="text-label mb-2">Chart series</div>
                <div className="flex gap-2 items-end" style={{ height: 60 }}>
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                        <div key={n} style={{ background: `var(--chart-series-${n})`, width: 32, height: `${30 + n * 8}%`, borderRadius: 4 }} />
                    ))}
                </div>
            </div>

            {/* Form + button row */}
            <div className="rounded-lg p-4"
                style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)" }}>
                <label className="text-xs font-semibold" style={{ color: "var(--label-text)" }}>Sample input</label>
                <input
                    type="text"
                    defaultValue="Payroll period name"
                    className="mt-1 w-full px-3 py-2 rounded"
                    style={{
                        background: "var(--input-bg)",
                        color: "var(--input-text)",
                        border: "1px solid var(--input-border)",
                    }}
                />
                <div className="flex gap-2 mt-3">
                    <button className="px-4 py-2 rounded text-sm font-semibold"
                        style={{ background: "var(--button-primary-bg)", color: "var(--button-primary-text)" }}>
                        Primary action
                    </button>
                    <button className="px-4 py-2 rounded text-sm"
                        style={{ background: "var(--button-secondary-bg)", color: "var(--button-secondary-text)", border: "1px solid var(--border-default)" }}>
                        Secondary
                    </button>
                    <a href="#preview" className="ml-auto self-center text-sm underline"
                        style={{ color: "var(--link-color)" }} onClick={(e) => e.preventDefault()}>
                        Learn more
                    </a>
                </div>
            </div>
        </div>
    );
}
